/**
 * What a waybill's STATUS and TYPE codes mean, and which ones may be received.
 *
 * Written 2026-09-16 when the owner sent Giorgi's real RS.GE list: six
 * waybills, one of them CANCELLED (გაუქმებული, 1004972002, ₾105) beside its
 * replacement (1004972007, ₾120 — same seller, same minute). Our receive
 * dialog never looked at STATUS, so it would have received both: ten cables
 * too many on the shelf and ₾105 of debt that does not exist.
 *
 * The rule the RS.GE skill insists on still holds — never HIDE a waybill on a
 * guessed status set; a wrong guess empties the list and looks like a broken
 * integration. So nothing is filtered: every waybill shows, with its status in
 * words, and only a NEGATIVE status (RS.GE's deleted/cancelled range) blocks
 * the receive button, with the reason on screen. An unknown code is shown as
 * its number, so a wrong assumption is visible instead of silent.
 *
 * Verified codes: STATUS 2 = დასრულებული and TYPE 6 = ქვე-ზედნადები (live,
 * 2026-09-02). The rest follow RS.GE's published list and are confirmed by
 * the labels on the owner's screenshots; anything else prints as a number.
 */

const STATUS: Record<string, string> = {
  '0': 'შენახული',
  '1': 'აქტიური',
  '2': 'დასრულებული',
  '-1': 'წაშლილი',
  '-2': 'გაუქმებული',
}

const TYPE: Record<string, string> = {
  '1': 'შიდა გადაზიდვა',
  '2': 'ტრანსპორტირებით',
  '3': 'ტრანსპორტირების გარეშე',
  '4': 'დისტრიბუცია',
  '5': 'უკან დაბრუნება',
  '6': 'ქვე-ზედნადები',
}

export function waybillStatusLabel(status: string | null | undefined): string {
  if (status == null || status === '') return 'სტატუსი უცნობი'
  return STATUS[status.trim()] ?? `სტატუსი ${status}`
}

export function waybillTypeLabel(type: string | null | undefined): string | null {
  if (type == null || type === '') return null
  return TYPE[type.trim()] ?? `ტიპი ${type}`
}

/** A cancelled or deleted waybill describes goods that did not arrive under
 *  it. Receiving it adds stock and supplier debt that do not exist. */
export function waybillIsVoid(status: string | null | undefined): boolean {
  if (status == null) return false
  const n = Number(status.trim())
  return Number.isFinite(n) && n < 0
}

/**
 * How a shop's OWN waybill stands, in our words. RS.GE's codes map onto it —
 * and the two negatives are NOT the same thing, which is what 102 is about:
 *   -1 წაშლილი    a DRAFT thrown away; it never was a document
 *   -2 გაუქმებული an ACTIVE waybill withdrawn; the buyer had seen it
 */
export type OutgoingStatus = 'draft' | 'sent' | 'closed' | 'cancelled' | 'deleted'

/**
 * RS.GE's status code → ours. ONE place: this was written twice in the store
 * and both copies made the same mistake, sending every negative to
 * „cancelled". `null` means a code we do not recognise — keep whatever was
 * already stored rather than invent a state for a tax document.
 */
export function rsStatus(n: number): OutgoingStatus | null {
  if (!Number.isFinite(n)) return null
  if (n === 0) return 'draft'
  if (n === 1) return 'sent'
  if (n === 2) return 'closed'
  if (n === -1) return 'deleted'
  if (n < 0) return 'cancelled'
  return null
}
